<template>
  <Message
    v-if="props.data.disclaimer"
    :pt-options="{
      variant: 'infoBanner'
    }"
    class="mb-6"
  >
    <div
      v-html="props.data.disclaimer"
    ></div>
  </Message>

  <div
    v-if="projectsStatus === 'pending' && !projectaData?.data?.projects?.length"
    class="flex flex-wrap -mx-3 -my-3"
  >
    <div
      v-for="n in 6"
      :key="n"
      class="px-3 py-3 w-1/2 flex-[1_0_50%]"
    >
      <Skeleton
        class="!w-full !h-[16rem]"
      >
      </Skeleton>
    </div>
  </div>

  <template v-if="projectaData?.data">
    <template v-if="projectaData?.data?.projects?.length">
      <div class="grid grid-cols-2 gap-6">
        <div
          v-for="project in projectaData.data.projects"
          :key="`project_short_${project.id}`"
          class="flex flex-col justify-between w-full h-full p-2 bg-white border border-gray-300 rounded shadow-md hover:border-secondary hover:text-secondary"
        >
          <div class="relative mb-4">
            <div class="relative flex justify-center items-center overflow-hidden w-full pt-[70%] rounded">
              <nuxt-picture
                preload
                :img-attrs="{class: 'absolute top-0 left-0 w-full h-full object-cover'}"
                :src="project.image ? project.image : '/img/missing.png'"
                :placeholder="[100, 50, 10]"
                sizes=""
              />
            </div>

            <div
              v-if="project.is_stock"
              class="absolute bottom-0 left-0 w-full px-4 py-1 text-white bg-primary text-sm"
            >
              <template v-if="project.stock_count > 0">
                {{ project.stock_count }} {{ $t('pcs') }} {{ $t('inStock') }}
              </template>
              <template v-else>
                {{ $t('outOfStock') }}
              </template>
            </div>
          </div>

          <div
            v-if="!project.projectIsActive"
            class="pl-2 mb-2 text-xs text-red-500 text-center"
          >
            <span
              v-html="$t('projectNotAvailable')"
            ></span>
          </div>

          <div>
            <label v-show="props.data.type === 'EM' || props.data.type === 'RP'">
              <fieldset class="flex items-center mb-2">
                <Checkbox
                  v-model="selectedItemsIds"
                  :disabled="props.data.disabled || !project.projectIsActive"
                  :value="project.id"
                  class="projectCheckbox small"
                />
                <span class="pl-2 text-xs font-bold">{{ project.name }}</span>
              </fieldset>
            </label>

            <label>
              <fieldset class="flex items-center pt-2">
                <RadioButton
                  v-model="selectedItemFeatured"
                  :disabled="props.data.disabled || !project.projectIsActive"
                  :value="project.id"
                  class="projectCheckbox small"
                />
                <span
                  v-if="props.data.type === 'EM' || props.data.type === 'RP'"
                  class="pl-2 text-xs"
                >
                  <span>{{ $t('featured') }}</span>
                </span>
                <span
                  v-else
                  class="pl-2 text-xs font-bold"
                >
                  {{ project.name }}
                </span>
              </fieldset>
            </label>
          </div>

          <div class="mt-4 text-right">
            <div>
              <span class="font-medium">{{ moneyFormat(project.total) }}</span>
            </div>
            <div class="text-xs text-gray-500">
              {{ $t('shippingAndTaxes') }}
            </div>
          </div>
        </div>
      </div>

      <div class="flex justify-center pt-4">
        <WidgetsSystemProductsLoadMore
          v-if="projectaData.data && (projectaData?._meta?.links?.limit * projectaData?._meta?.links?.page) < projectaData?._meta?.links?.total
            && projectaData?._meta?.links?.limit < projectaData?._meta?.links?.total"
          :autoloading="true"
          :is-load-more="isLoadMore"
          :params-products="paramsProjects"
          :products-pending="projectsStatus === 'pending'"
          :products="projectaData"
          @load-more-products="e => {
            paramsProjects.page = e.page
            isLoadMore = true

            isUpdateParams = true
            refresh().then(() => {
              isUpdateParams = false
              isLoadMore = false
            })
          }"
        />
      </div>
    </template>

    <template v-else>
      <Message
        severity="info"
      >
        {{ $t('projectsIsEmptyForGifts') }}
      </Message>
    </template>
  </template>
</template>

<script setup>
const { $i18n: i18n } = useNuxtApp()
const toast = useToast()
const { confirmOptions } = usePopups()

const { getProjects, paramsProjects } = useCustomerProjects({ key: 'short' })

const selectedItemsIds = ref([])
const selectedItemFeatured = ref(false)
const isLoadMore = ref(false)
const isUpdateParams = ref(false)

// Store previous values to detect changes
const previousSelectedIds = ref([])
const hasProjectsChanged = ref(false)

const originalSelectedIds = ref([])

const returnData = ref({})

/**
* Props
*/
const props = defineProps({
  id: {
    type: [String],
    default: null
  },
  data: {
    type: [Object],
    default: null
  }
})

const alertForActive = ref(props.data?.additionalOptions?.showAlert ?? false)

/**
* Emits
*/
const emit = defineEmits(['returnData', 'isPending', 'has-alcohol-changed', 'project-saved'])

/**
* Selected Items
*/
if (Array.isArray(props.data?.formData[props.data.name])) {
  const selectedProjectArray = (props.data?.availableProjects)
    ? props.data.formData[props.data.name].filter(n => props.data?.availableProjects.includes(n))
    : props.data.formData[props.data.name]

  selectedItemsIds.value = selectedProjectArray.filter(n => n !== 0 && n !== 'undefined' && n !== undefined)

  selectedItemFeatured.value = props.data.formData[props.data.secondName]
    ? props.data.formData[props.data.secondName]
    : false

  previousSelectedIds.value = [...selectedItemsIds.value]
  originalSelectedIds.value = [...selectedItemsIds.value]
}

/**
* Get Projects
*/
paramsProjects.value = {
  limit: 35,
  page: 1,
  filters: {
    isSlp: true,
    isShort: true,
    selected_ids: selectedItemsIds.value.length > 0 ? selectedItemsIds.value.join(',') : undefined
  }
}

const { data: projectaData, refresh, status: projectsStatus } = getProjects()

// Flag to prevent watcher loops
const isUpdatingFromProjectData = ref(false)

/**
 * Handle personalization warnings and save functionality
 */
const handlePersonalizationSubmission = (onContinue) => {
  const originalProjectsSupportsPersonalization = () => {
    if (!projectaData.value?.data?.projects || originalSelectedIds.value.length === 0) return false
    return originalSelectedIds.value.some(projectId => {
      const project = projectaData.value.data.projects.find(p => p.id === projectId)
      return project && !project.hide_personalization && project.has_personalization_questions
    })
  }
  
  if (hasProjectsChanged.value && props.data.type === 'FF') {
    const supportsPersonalization = originalProjectsSupportsPersonalization()
    
    let message = `<div class="mb-4">${i18n?.t('projectsChangedSubmissionWarning')}</div>`
    message += `<div class="text-sm text-gray-600 mb-4">${i18n?.t('projectsChangedSubmissionHint')}</div>`
    
    if (supportsPersonalization) {
      message += `<div class="mb-4 p-3 bg-blue-50 border border-blue-200 rounded">
        <p class="text-sm text-blue-800">${i18n?.t('changeProjectPersonalizationSubmissionWarning')}</p>
      </div>`
    }
    
    confirmOptions({
      header: i18n?.t('projectChangesDetected'),
      message: message,
      acceptProps: { label: i18n?.t('saveAndSubmit'), severity: 'primary' },
      rejectProps: { label: i18n?.t('submitWithoutSaving'), severity: 'secondary', outlined: true },
      accept: () => saveCampaignPersonalization().then(() => onContinue?.()),
      reject: () => onContinue?.()
    })
  } else {
    onContinue?.()
  }
}

/**
 * Save campaign personalization data
 */
const saveCampaignPersonalization = async () => {
  try {
    const campaignId = props.data.additionalOptions?.campaignId || 
                      props.data.additionalOptions?.additionalData?.id || 
                      props.data.formData?.id
    
    if (!campaignId) {
      toast.add({
        severity: 'error',
        summary: 'Save Error',
        detail: 'Campaign ID not found. Cannot save personalization.',
        life: 3000
      })
      return
    }

    const campaignData = {
      ...props.data.formData,
      [props.data.name]: selectedItemsIds.value,
      [props.data.secondName]: selectedItemFeatured.value,
      all_premade_greeting_card: allPremadeGreetingCards.value,
      status: props.data.formData?.status || 'New',
      agree: true
    }
    
    if (props.data.additionalFields) {
      props.data.additionalFields.forEach(field => {
        if (returnData.value[field.name] !== undefined) {
          campaignData[field.name] = returnData.value[field.name]
        }
      })
    }

    await useEditCampaign(campaignId, campaignData)
    
    hasProjectsChanged.value = false
    originalSelectedIds.value = [...selectedItemsIds.value]
    
    // Emit event to trigger recipient tab refresh
    emit('project-saved', { campaignId, refreshRecipients: true })
    
    emit('returnData', {
      selectedProjects: selectedItems.value,
      [props.data.name]: selectedItemsIds.value,
      [props.data.secondName]: selectedItemFeatured.value,
      all_premade_greeting_card: allPremadeGreetingCards.value,
      hasAlcohol: returnData.value.hasAlcohol
    })
    
    // Direct refresh approach for drawer contexts
    if (process.client && window.refreshRecipientsAfterProjectSave) {
      try {
        await window.refreshRecipientsAfterProjectSave()
      } catch (error) {
        console.error('Error calling window refresh function:', error)
      }
    }
  } catch (error) {
    console.error('Save campaign error:', error)
    
    toast.add({
      severity: 'error',
      summary: 'Save Error',
      detail: 'Failed to save campaign personalization. Please try again.',
      life: 5000
    })
  }
}

/*
* Update selected projects when data changing
*/
watch(projectaData, (newA) => {
  if (!newA || !newA.data || !newA.data.projects) {
    return
  }

  // Prevent infinite loops
  isUpdatingFromProjectData.value = true

  const selectedProjectArray = newA.data.projects.filter(n => selectedItemsIds.value.includes(n.id) && n !== 0 && n.projectIsActive)

  selectedItemsIds.value = selectedProjectArray.map(n => n.id)

  selectedItemFeatured.value = selectedProjectArray.find(n => n.id === selectedItemFeatured.value)
    ? selectedItemFeatured.value
    : selectedItemsIds.value[0]

  // Reset flag after Vue's next tick
  nextTick(() => {
    isUpdatingFromProjectData.value = false
  })
}, { deep: true, immediate: true })

/*
* Block "Done" button if projects is loading
*/
watch(projectsStatus, (newA) => {
  emit('isPending', newA === 'pending')
}, { deep: true, immediate: true })

/**
* Selectes Names
*/
const selectedItems = computed({
  get() {
    const namesArr = []

    if (projectaData.value?.data?.projects) {
      selectedItemsIds.value.forEach((id) => {
        const item = projectaData.value?.data?.projects?.find(n => n.id === id)
        namesArr.push(item)
      })
    }

    return namesArr
  }
})

/**
* checks if all selected projects have premade greeting cards
*/
const allPremadeGreetingCards = computed({
  get() {
    let allPremadeGreetingCard = true

    selectedItemsIds.value.forEach((id) => {
      if(!projectaData.value?.data?.projects?.find(n => n.id === id)?.premade_greeting_card) {
        allPremadeGreetingCard = false
      }
    })

    return allPremadeGreetingCard
  }
})

/**
* Change Selected Items
*/
watch(selectedItemsIds, async (newA, oldA) => {
  // Skip if this change is coming from projectaData watcher to prevent infinite loops
  if (isUpdatingFromProjectData.value) {
    return
  }

  const hasChangedFromOriginal = (
    newA.length !== originalSelectedIds.value.length || 
    !newA.every(id => originalSelectedIds.value.includes(id)) ||
    !originalSelectedIds.value.every(id => newA.includes(id))
  )
  
  hasProjectsChanged.value = hasChangedFromOriginal

  // Apply changes normally
  applyProjectChangesNormally(newA, oldA)
}, { deep: true })

/**
 * Apply project changes normally (without warning)
 */
const applyProjectChangesNormally = (newA, oldA) => {
  // Featured
  if (newA.length) {
    if (!selectedItemFeatured.value) {
      selectedItemFeatured.value = newA[0]
    } else if (!newA.includes(selectedItemFeatured.value)) {
      selectedItemFeatured.value = newA[0]
    }
  } else {
    selectedItemFeatured.value = false
  }

  // Show Toast if customer unchecks a project in an active campaign or redeem
  const unchecked = oldA.filter(id => !newA.includes(id))
  if (alertForActive.value && unchecked.length > 0) {
    alertForActive.value = false
    toast.add({
      severity: 'warn',
      detail: (props.data.type === 'RP') ? i18n?.t('changeProjectRedeemWarning') : i18n?.t('changeProjectCampaignWarning'),
      life: 5000
    })
  }

  previousSelectedIds.value = [...newA]

  returnData.value[props.data.name] = selectedItemsIds.value
  returnData.value[props.data.secondName] = selectedItemFeatured.value

  if (props.data.additionalFields?.find(n => n.name === 'selectedProjects')) {
    returnData.value['selectedProjects'] = selectedItems.value
  }

  if (props.data.additionalFields?.find(n => n.name === 'all_premade_greeting_card')) {
    returnData.value.all_premade_greeting_card = allPremadeGreetingCards.value
  }

  let hasAlcohol = false
  if (Array.isArray(newA) && props.data?.availableProjects) {
    for (const id of newA) {
      const project = projectaData.value?.data?.projects.find(p => p.id === id)
      if (project?.has_alcohol) {
        hasAlcohol = true
        break
      }
    }
  }
  returnData.value.hasAlcohol = hasAlcohol

  emit('returnData', returnData.value)
}

/**
* Change Featured Item
*/
watch([selectedItemFeatured, projectsStatus], ([newA, newB]) => {
  if (newA && newB === 'success') {
    if (props.data.type === 'EM' || props.data.type === 'RP') {
      if (!selectedItemsIds.value.includes(newA) && newA) {
        selectedItemsIds.value.push(newA)
      }
    } else {
      selectedItemsIds.value = [newA]
    }

    returnData.value[props.data.name] = selectedItemsIds.value
    returnData.value[props.data.secondName] = selectedItemFeatured.value

    if (props.data.additionalFields?.find(n => n.name === 'selectedProjects')) {
      returnData.value['selectedProjects'] = selectedItems.value
    }

    if (props.data.additionalFields?.find(n => n.name === 'all_premade_greeting_card')) {
      returnData.value.all_premade_greeting_card = allPremadeGreetingCards.value
    }

    emit('returnData', returnData.value)
  }
}, { deep: true })

/**
 * Download personalization data as Excel file using the same API as the campaign system
 */
const downloadPersonalizationData = async () => {
  try {
    const campaignId = props.data.additionalOptions?.campaignId || 
                      props.data.additionalOptions?.additionalData?.id || 
                      props.data.formData?.id
    
    if (!campaignId) {
      toast.add({
        severity: 'error',
        summary: 'Download Error',
        detail: 'Campaign ID not found. Cannot download personalization data.',
        life: 3000
      })
      return
    }
    
    await downloadFile(
      useGetCampaignsDownloads('campaignPersonalizations', campaignId), 
      `campaignPersonalizations-${campaignId}-${Date.now()}.xlsx`
    )
    
    toast.add({
      severity: 'success',
      summary: i18n?.t('downloadPersonalizationFirst'),
      detail: 'Personalization data has been downloaded successfully',
      life: 3000
    })
  } catch (error) {
    console.error('Download error:', error)
    toast.add({
      severity: 'error',
      summary: 'Download Error',
      detail: 'Failed to download personalization data. Please try again.',
      life: 3000
    })
  }
}

// Global registration and component exposure
if (import.meta.client) {
  window.downloadPersonalizationData = downloadPersonalizationData
  
  onMounted(() => {
    window.currentProjectsComponent = {
      checkProjectChangesOnSubmit: handlePersonalizationSubmission,
      resetProjectChangesFlag: () => { hasProjectsChanged.value = false }
    }
  })
  
  onUnmounted(() => {
    if (window.currentProjectsComponent) {
      delete window.currentProjectsComponent
    }
  })
}

defineExpose({
  checkProjectChangesOnSubmit: handlePersonalizationSubmission,
  resetProjectChangesFlag: () => { hasProjectsChanged.value = false }
})
</script>
